import { Controller } from "@hotwired/stimulus"

/**
 * Dropdown Controller - Reusable dropdown/select menu
 *
 * Implements WCAG 2.1 AA listbox pattern with keyboard navigation
 * https://www.w3.org/WAI/ARIA/apg/patterns/listbox/
 *
 * Usage:
 * <div data-controller="dropdown">
 *   <button data-action="click->dropdown#toggle" data-dropdown-target="trigger">
 *     <span data-dropdown-target="label">Selected Value</span>
 *     <svg data-dropdown-target="icon">...</svg>
 *   </button>
 *   <div data-dropdown-target="menu" class="hidden" role="listbox">
 *     <button data-action="click->dropdown#select" data-value="option1" role="option">
 *       Option 1
 *     </button>
 *   </div>
 * </div>
 *
 * Events dispatched:
 * - dropdown:change - When selection changes (detail: { value, label })
 */
export default class extends Controller {
  static targets = ["trigger", "menu", "label", "icon"]

  static values = {
    open: { type: Boolean, default: false },
    closeOnSelect: { type: Boolean, default: true }
  }

  connect() {
    this.boundHandleOutsideClick = this.handleOutsideClick.bind(this)
    this.boundHandleKeydown = this.handleKeydown.bind(this)

    document.addEventListener("click", this.boundHandleOutsideClick)
  }

  disconnect() {
    document.removeEventListener("click", this.boundHandleOutsideClick)
    this.close()
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.openValue) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    this.openValue = true
    this.menuTarget.classList.remove("hidden")

    // Update ARIA attributes
    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-expanded", "true")
    }

    // Rotate icon if exists
    if (this.hasIconTarget) {
      this.iconTarget.classList.add("rotate-180")
    }

    // Add keyboard navigation
    document.addEventListener("keydown", this.boundHandleKeydown)

    // Focus first option
    this.focusFirstOption()
  }

  close() {
    this.openValue = false
    this.menuTarget.classList.add("hidden")

    // Update ARIA attributes
    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-expanded", "false")
    }

    // Reset icon rotation
    if (this.hasIconTarget) {
      this.iconTarget.classList.remove("rotate-180")
    }

    // Remove keyboard navigation
    document.removeEventListener("keydown", this.boundHandleKeydown)
  }

  select(event) {
    event.preventDefault()
    const button = event.currentTarget
    const value = button.dataset.value || button.dataset.sortValue
    const label = button.textContent.trim()

    // Update label display
    if (this.hasLabelTarget) {
      this.labelTarget.textContent = label
    }

    // Update aria-selected on all options
    const options = this.menuTarget.querySelectorAll("[role='option']")
    options.forEach(option => {
      option.setAttribute("aria-selected", option === button ? "true" : "false")
    })

    // Dispatch custom event for parent controllers
    this.dispatch("change", {
      detail: { value, label },
      bubbles: true
    })

    // Close menu if configured
    if (this.closeOnSelectValue) {
      this.close()
    }
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target) && this.openValue) {
      this.close()
    }
  }

  handleKeydown(event) {
    const options = Array.from(this.menuTarget.querySelectorAll("[role='option']"))
    const currentIndex = options.findIndex(opt => opt === document.activeElement)

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.focusOption(options, currentIndex + 1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.focusOption(options, currentIndex - 1)
        break
      case "Enter":
      case " ":
        event.preventDefault()
        if (document.activeElement.matches("[role='option']")) {
          document.activeElement.click()
        }
        break
      case "Escape":
        this.close()
        if (this.hasTriggerTarget) {
          this.triggerTarget.focus()
        }
        break
      case "Tab":
        this.close()
        break
    }
  }

  focusFirstOption() {
    const firstOption = this.menuTarget.querySelector("[role='option']")
    if (firstOption) {
      firstOption.focus()
    }
  }

  focusOption(options, index) {
    // Wrap around
    if (index < 0) index = options.length - 1
    if (index >= options.length) index = 0

    if (options[index]) {
      options[index].focus()
    }
  }
}
